// @generated by web/tools/codegen — DO NOT EDIT

/**
 * The CLOSED page-chrome vocabulary and THE table — generated from the
 * `PageChrome` enum webroutesgen parses `web/manifests/routes.yaml`'s
 * `chrome:` with, and from the exhaustive `PageChrome::spec()` match beside
 * it, so the union, the manifest and the rendered bands cannot disagree.
 *
 * - `hub`: a root destination: navbar, footer (md+), bottom tabs (<md)
 * - `article`: a content page that owns its bottom edge: navbar + footer, no tabs
 * - `focus`: the page owns its bottom edge: navbar only, no footer, no tabs
 * - `panel`: a drilled-into surface that IS the viewport: navbar + mobile back band, no footer, no tabs - the page's own panes scroll inside a height-bounded main
 * - `inbox`: a tab root that is not a content page: navbar + bottom tabs, no footer, and the viewport bounded - its list scrolls inside the page
 * - `canvas`: a viewport-sized working surface (map + results): navbar full-bleed, no footer, no tabs - the page owns the rest of the viewport and does not scroll the document
 * - `flow`: no site chrome at all - the page owns the whole viewport
 */
export const PAGE_CHROME_KINDS = ['hub', 'article', 'focus', 'panel', 'inbox', 'canvas', 'flow'] as const;

/** One of {@link PAGE_CHROME_KINDS}. */
export type PageChrome = (typeof PAGE_CHROME_KINDS)[number];

/**
 * Narrow a route match's `handle.chrome`. React Router types `handle`
 * as `unknown`, so the shell owns the narrowing — and a value outside
 * the vocabulary must THROW rather than default, because a default
 * renders some chrome for a route whose chrome nobody decided.
 */
export function isPageChrome(value: unknown): value is PageChrome {
  return (PAGE_CHROME_KINDS as readonly unknown[]).includes(value);
}

/**
 * What a chrome kind RENDERS. Every field is a band at a page edge, or the
 * geometry those bands and the page share.
 */
export interface ChromeSpec {
  /** Which navbar, if any. `site` is `SiteNavbar` (itself `hidden md:block`). */
  readonly navbar: 'site' | 'none';
  /**
   * The column the navbar's content and the page SHARE. `contained` is the
   * site's `max-w-7xl` reading column; `bleed` is the viewport. One field,
   * because it is one decision: a full-bleed page under a contained navbar
   * is the obs. 1 defect (navbar inner width 1280, body width 1600).
   */
  readonly width: 'contained' | 'bleed';
  /**
   * `flow`: the DOCUMENT scrolls. `fixed`: the page IS the viewport and its
   * own panes scroll inside it. Measured on the hub while it was `article`:
   * `scrollHeight` 1398 against a 900 viewport.
   */
  readonly viewport: 'flow' | 'fixed';
  /** The public `Footer`, desktop only — every layout wrapped it `hidden md:block`. */
  readonly footer: boolean;
  /** The four-tab `MobileBottomNav`, mobile only (it is `md:hidden`). */
  readonly bottomTabs: boolean;
  /**
   * The MOBILE top band (`md:hidden`) — who draws it, and therefore who owes
   * a back target.
   *
   * `back`: the SHELL draws `MobileTopBar` with a history-aware `BackLink`.
   * `page`: the PAGE draws its own top bar carrying the shared `BackLink`
   * (the browse canvas's `ListingsTopBar`) — the shell draws no band, but it
   * still resolves the target and hands it down, so the destination is
   * declared in the manifest like every other one.
   * `none`: neither.
   *
   * Anything but `none` is a kind `webroutesgen` REQUIRES a `back:` route id
   * from — see {@link CHROME_KINDS_WITH_BACK_TARGET}, which is DERIVED from
   * this column rather than declared beside it.
   */
  readonly mobileHeader: 'none' | 'back' | 'page';
  /**
   * Whether the page DECLARES the OS-standard native-app promotion banner
   * (the iOS Smart App Banner meta). The banner a reader sees is drawn by
   * the OS off the site's total deep-link association; what is ours is WHERE
   * the site asks for it, and that is a property of page chrome.
   */
  readonly appBanner: boolean;
}

/**
 * THE table. One row per kind, emitted from the generator's own
 * `PageChrome::spec()` match — so `PageShell` COMPOSES chrome and decides
 * none of it, and the e2e's `web/e2e/page-chrome_gen.ts` is the same rows
 * rather than a hand copy of them.
 */
export const PAGE_CHROME = {
  hub: { navbar: 'site', width: 'contained', viewport: 'flow', footer: true, bottomTabs: true, mobileHeader: 'none', appBanner: true },
  article: { navbar: 'site', width: 'contained', viewport: 'flow', footer: true, bottomTabs: false, mobileHeader: 'back', appBanner: false },
  focus: { navbar: 'site', width: 'contained', viewport: 'flow', footer: false, bottomTabs: false, mobileHeader: 'back', appBanner: false },
  panel: { navbar: 'site', width: 'contained', viewport: 'fixed', footer: false, bottomTabs: false, mobileHeader: 'back', appBanner: false },
  inbox: { navbar: 'site', width: 'contained', viewport: 'fixed', footer: false, bottomTabs: true, mobileHeader: 'none', appBanner: false },
  canvas: { navbar: 'site', width: 'bleed', viewport: 'fixed', footer: false, bottomTabs: false, mobileHeader: 'page', appBanner: false },
  flow: { navbar: 'none', width: 'bleed', viewport: 'flow', footer: false, bottomTabs: false, mobileHeader: 'none', appBanner: false },
} as const satisfies Record<PageChrome, ChromeSpec>;

/**
 * The kinds whose pages carry a back control — the shell's own band or
 * the page's — and which the generator therefore REQUIRES a `back:` route
 * id from, on every node that resolves to one.
 *
 * DERIVED from the `mobileHeader` column above, in the generator, so this
 * is a projection of the table and not a second declaration to keep equal
 * to it. It used to be the latter, welded by a jsdom assertion.
 */
export const CHROME_KINDS_WITH_BACK_TARGET = ['article', 'focus', 'panel', 'canvas'] as const;

/**
 * The `mobileHeader` values that mean THE READER HAS A WAY BACK —
 * `back` (the shell's own band) and `page` (the browse canvas's own top
 * bar). Emitted rather than spelled at the call site for two reasons: it
 * is the generator's own partition of the column, and the estate's
 * `no-raw-wire-alias-string` rule refuses a direct comparison against the
 * third value, which is a registered wire alias.
 */
export const CHROME_MOBILE_HEADERS_WITH_BACK_CONTROL = ['back', 'page'] as const;
